import axios from 'axios';
import axiosInstance from '../services/axiosConfig';

// Get base URLs from environment
const BASE_URL = process.env.REACT_APP_TASK_API_BASE_URL;

// Helper: make API request to Task API
const tryRequest = async (path, config = {}, method = 'get', data) => {
  const url = `${BASE_URL}${path}`;
  if (method === 'get') return (await axios.get(url, config)).data;
  if (method === 'delete') return (await axios.delete(url, config)).data;
  if (method === 'put') return (await axios.put(url, data, config)).data;
  if (method === 'post') return (await axios.post(url, data, config)).data;
};

// Helper: make API request to RMMS API (for datasource, tags, etc.) - uses axiosInstance with JWT
const tryRmmsRequest = async (path, config = {}, method = 'get', data) => {
  console.log(`[tryRmmsRequest] ${method.toUpperCase()} ${path}`, data);
  try {
    let response;
    if (method === 'get') response = await axiosInstance.get(path, config);
    else if (method === 'delete') response = await axiosInstance.delete(path, config);
    else if (method === 'put') response = await axiosInstance.put(path, data, config);
    else if (method === 'post') response = await axiosInstance.post(path, data, config);
    console.log(`[tryRmmsRequest] Response:`, response?.status, response?.data);
    return response?.data;
  } catch (error) {
    console.error(`[tryRmmsRequest] Error:`, error.message, error.response?.status, error.response?.data);
    throw error;
  }
};

// Read runtime context from storage (no mocks)
const getContext = () => {
  const companyRaw = sessionStorage.getItem('TEMPCOM') || localStorage.getItem('TEMPCOM') || localStorage.getItem('temp_com_id');
  const userRaw = localStorage.getItem('user_id') || sessionStorage.getItem('user_id');
  const companyId = companyRaw ? parseInt(JSON.parse(companyRaw || 'null')) || parseInt(companyRaw) : null;
  const userId = userRaw ? parseInt(userRaw) || userRaw : null;
  if (!companyId) throw new Error('Şirket (CompanyId) bulunamadı. Lütfen Tesis seçin.');
  return { companyId, userId };
};

const api = {
  // Variables (Tags + Vars)
  getVariables: async () => {
    const { companyId, userId } = getContext();
    const payload = await tryRequest(
      `/Task/variables/${companyId}`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'get'
    ) || {};
    const vars = (payload.Vars || payload.vars || []).map(v => ({
      id: `var_${v.VarId || v.varId || v.id}`,
      name: v.VarName || v.varName || v.name,
      type: 'V',
      varID: v.VarId || v.varId || v.id,
    }));
    const tags = (payload.Tags || payload.tags || []).map(t => ({
      id: `tag_${t.TagID || t.tagID || t.id}`,
      name: t.TagName || t.tagName || t.name,
      type: 'T',
      tagID: t.TagID || t.tagID || t.id,
    }));
    return [...vars, ...tags];
  },

  // Workflows
  listWorkflows: async () => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/Task/workflows/${companyId}`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'get'
    );
  },
  deleteWorkflow: async (name) => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/Task/workflows/${companyId}/${encodeURIComponent(name)}`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'delete'
    );
  },
  checkWorkflowUsage: async (name) => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/Task/workflows/${companyId}/${encodeURIComponent(name)}/usage`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'get'
    );
  },
  getWorkflow: async (name) => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/Task/workflows/${companyId}/${encodeURIComponent(name)}`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'get'
    );
  },
  saveWorkflow: async ({ name, json, description = '', category = '' }) => {
    const { companyId, userId } = getContext();
    const body = {
      Name: name,
      Description: description,
      Category: category,
      Json: typeof json === 'string' ? json : JSON.stringify(json),
      CompanyId: companyId,
      UserId: userId,
    };
    // Try update, else create
    try {
      await tryRequest(
        `/Task/workflows/${encodeURIComponent(name)}`,
        { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
        'put',
        body
      );
      return true;
    } catch (err) {
      if (err.response?.status !== 404) throw err;
      await tryRequest(
        `/Task/workflows`,
        { headers: { 'Content-Type': 'application/json', 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
        'post',
        body
      );
      return true;
    }
  },
  updateWorkflowContent: async (name, content) => {
    const { companyId, userId } = getContext();
    const json = typeof content === 'string' ? content : JSON.stringify(content);
    return await tryRequest(
      `/Task/workflows/${companyId}/${encodeURIComponent(name)}/content`,
      { headers: { 'Content-Type': 'application/json', 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'put',
      json
    );
  },

  // Task Management
  getSavedTasks: async () => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/Task/tasks/${companyId}`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'get'
    );
  },
  startTask: async (taskId) => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/Task/tasks/${companyId}/${taskId}/start`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'post',
      {}
    );
  },
  stopTask: async (taskId) => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/Task/tasks/${companyId}/${taskId}/stop`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'post',
      {}
    );
  },
  updateTask: async (taskName, taskData) => {
    const { companyId, userId } = getContext();
    const body = {
      Name: taskData.Name || taskData.name || taskName,
      Type: taskData.Type || taskData.taskType || 'workflow',
      ExecutionLocation: taskData.ExecutionLocation || taskData.executionLocation || 'local',
      ExecutionMode: taskData.ExecutionMode || taskData.executionMode || 'scheduler',
      IsEnabled: taskData.IsEnabled !== undefined ? taskData.IsEnabled : taskData.isEnabled !== undefined ? taskData.isEnabled : true,
      Configuration: typeof taskData.Configuration === 'string' ? taskData.Configuration :
                     typeof taskData.configuration === 'string' ? taskData.configuration :
                     JSON.stringify(taskData.Configuration || taskData.configuration || {}),
      ContentName: taskData.ContentName || taskData.contentName || '',
      CompanyId: companyId,
      UserId: userId
    };
    return await tryRequest(
      `/Task/tasks/${companyId}/${encodeURIComponent(taskName)}`,
      { headers: { 'Content-Type': 'application/json', 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'put',
      body
    );
  },
  createTask: async (taskData) => {
    const { companyId, userId } = getContext();
    const body = {
      Name: taskData.Name || taskData.name,
      Type: taskData.Type || taskData.taskType || 'workflow',
      ExecutionLocation: taskData.ExecutionLocation || taskData.executionLocation || 'local',
      ExecutionMode: taskData.ExecutionMode || taskData.executionMode || 'scheduler',
      IsEnabled: taskData.IsEnabled !== undefined ? taskData.IsEnabled : taskData.isEnabled !== undefined ? taskData.isEnabled : true,
      Configuration: typeof taskData.Configuration === 'string' ? taskData.Configuration :
                     typeof taskData.configuration === 'string' ? taskData.configuration :
                     JSON.stringify(taskData.Configuration || taskData.configuration || {}),
      ContentName: taskData.ContentName || taskData.contentName || '',
      CompanyId: companyId,
      UserId: userId
    };
    return await tryRequest(
      `/Task/tasks`,
      { headers: { 'Content-Type': 'application/json', 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'post',
      body
    );
  },
  deleteTask: async (taskName) => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/Task/tasks/${companyId}/${encodeURIComponent(taskName)}`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'delete'
    );
  },
  getLastWorkflowError: async () => {
    const { companyId, userId } = getContext();
    try {
      return await tryRequest(
        `/Task/workflows/last-error/${companyId}`,
        { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
        'get'
      );
    } catch (error) {
      console.error('Error fetching last workflow error:', error);
      return null;
    }
  },

  // Alias for compatibility with Axis TemplateManager
  getWorkflows: async () => {
    return await api.listWorkflows();
  },

  // Codes (from RMMS API, not MetisEngine)
  getCodes: async () => {
    const { companyId, userId } = getContext();
    const response = await axiosInstance.get(`/Flow/codes/${companyId}`, {
      headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined }
    });
    return response.data;
  },

  // Template mappings
  getAllTagTemplateStatus: async () => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/Task/tags/template-status/${companyId}`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'get'
    );
  },

  // Web Services Operations
  getAllServices: async () => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/WebServices/${companyId}`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'get'
    );
  },

  getServiceById: async (id) => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/WebServices/${companyId}/${id}`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'get'
    );
  },

  createService: async (serviceData) => {
    const { userId, companyId } = getContext();

    // Process methods and objects to ensure proper format
    const processedMethods = serviceData.methods.map(method => {
      return {
        name: method.name,
        description: method.description || '',
        type: method.type || serviceData.type,
        httpMethod: method.httpMethod,
        path: method.path,
        soapAction: method.soapAction,
        namespace: method.namespace,
        parameters: typeof method.parameters === 'string'
          ? method.parameters
          : JSON.stringify(method.parameters || []),
        responses: typeof method.responses === 'string'
          ? method.responses
          : JSON.stringify(method.responses || []),
        returnType: method.returnType,
        returnObjectRef: method.returnObjectRef,
        isArrayReturn: method.isArrayReturn
      };
    });

    // Process objects to ensure proper format
    const processedObjects = serviceData.objects.map(obj => {
      return {
        name: obj.name,
        type: obj.type || 'object',
        description: obj.description || '',
        isRequest: Boolean(obj.isRequest),
        isResponse: Boolean(obj.isResponse),
        properties: typeof obj.properties === 'string'
          ? obj.properties
          : JSON.stringify(obj.properties || [])
      };
    });

    // Prepare the final payload
    const formattedData = {
      name: serviceData.name,
      type: serviceData.type,
      url: serviceData.url,
      swaggerUrl: serviceData.swaggerUrl || '',
      wsdlUrl: serviceData.wsdlUrl || '',
      description: serviceData.description || '',
      authenticationType: serviceData.authenticationType || serviceData.authentication?.type,
      authenticationConfig: typeof serviceData.authenticationConfig === 'string'
        ? serviceData.authenticationConfig
        : JSON.stringify(serviceData.authentication || {}),
      saveToDb: serviceData.saveToDb,
      timeout: serviceData.timeout,
      companyId: companyId,
      userId: userId,
      methods: processedMethods,
      objects: processedObjects
    };

    console.log('Creating service with formatted data:', JSON.stringify(formattedData, null, 2));
    return await tryRequest(
      `/WebServices/${companyId}`,
      { headers: { 'Content-Type': 'application/json', 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'post',
      formattedData
    );
  },

  updateService: async (id, serviceData) => {
    const { userId, companyId } = getContext();

    // Process methods to ensure proper format
    const processedMethods = serviceData.methods.map(method => {
      return {
        name: method.name,
        description: method.description || '',
        type: method.type || serviceData.type,
        httpMethod: method.httpMethod,
        path: method.path,
        soapAction: method.soapAction,
        namespace: method.namespace,
        parameters: typeof method.parameters === 'string'
          ? method.parameters
          : JSON.stringify(method.parameters || []),
        responses: typeof method.responses === 'string'
          ? method.responses
          : JSON.stringify(method.responses || []),
        returnType: method.returnType,
        returnObjectRef: method.returnObjectRef,
        isArrayReturn: method.isArrayReturn
      };
    });

    // Process objects to ensure proper format
    const processedObjects = serviceData.objects.map(obj => {
      return {
        name: obj.name,
        type: obj.type || 'object',
        description: obj.description || '',
        isRequest: Boolean(obj.isRequest),
        isResponse: Boolean(obj.isResponse),
        properties: typeof obj.properties === 'string'
          ? obj.properties
          : JSON.stringify(obj.properties || [])
      };
    });

    // Prepare the final payload
    const formattedData = {
      id: parseInt(id),
      name: serviceData.name,
      type: serviceData.type,
      url: serviceData.url,
      swaggerUrl: serviceData.swaggerUrl || '',
      wsdlUrl: serviceData.wsdlUrl || '',
      description: serviceData.description || '',
      authenticationType: serviceData.authenticationType || serviceData.authentication?.type,
      authenticationConfig: typeof serviceData.authenticationConfig === 'string'
        ? serviceData.authenticationConfig
        : JSON.stringify(serviceData.authentication || {}),
      saveToDb: serviceData.saveToDb,
      timeout: serviceData.timeout,
      companyId: companyId,
      userId: userId,
      methods: processedMethods,
      objects: processedObjects
    };

    console.log('Updating service with formatted data:', JSON.stringify(formattedData, null, 2));
    return await tryRequest(
      `/WebServices/${companyId}/${id}`,
      { headers: { 'Content-Type': 'application/json', 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'put',
      formattedData
    );
  },

  deleteService: async (id) => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/WebServices/${companyId}/${id}`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'delete'
    );
  },

  discoverMethods: async (id) => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/WebServices/${companyId}/${id}/discover`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'post',
      {}
    );
  },

  testService: async (id) => {
    const { companyId, userId } = getContext();
    return await tryRequest(
      `/WebServices/${companyId}/${id}/test`,
      { headers: { 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
      'post',
      {}
    );
  },

  discoverServicePreview: async (payload) => {
    const { companyId, userId } = getContext();

    // Add companyId to the payload
    const updatedPayload = {
      ...payload,
      servicePreview: {
        ...payload.servicePreview,
        companyId: companyId
      }
    };

    console.log('Discovery request payload:', updatedPayload);
    try {
      const response = await tryRequest(
        `/WebServices/discover-preview`,
        { headers: { 'Content-Type': 'application/json', 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
        'post',
        updatedPayload
      );

      if (!response) {
        throw new Error('No data received from discovery');
      }

      console.log('Discovery response:', response);

      return {
        status: response.status,
        message: response.message,
        methods: response.methods || [],
        objects: response.objects || [],
        errors: response.errors || []
      };
    } catch (error) {
      console.error('Discovery error:', error);

      // Extract error message
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.error ||
                          error.message ||
                          'Service discovery failed';

      throw new Error(errorMessage);
    }
  },

  testServicePreview: async (serviceData) => {
    const { companyId, userId } = getContext();
    try {
      const response = await tryRequest(
        `/WebServices/${companyId}/${serviceData.id}/test`,
        { headers: { 'Content-Type': 'application/json', 'X-Company-Id': String(companyId), 'X-User-Id': userId ? String(userId) : undefined } },
        'post',
        serviceData
      );
      return response;
    } catch (error) {
      console.error('Test error:', error);
      throw new Error(error.response?.data?.message || 'Service test failed');
    }
  },

  // DataSources API (uses RMMS API base URL)
  dataSources: {
    // PLC Management
    getAllPlcs: async () => {
      const { companyId } = getContext();
      if (!companyId) throw new Error("Company ID is required");
      return await tryRmmsRequest(`/datasource/plc/list?companyId=${companyId}`, {}, 'get');
    },

    createPlc: async (plc) => {
      const { companyId } = getContext();
      const plcTypeMap = { 'SIEMENS': 1, 'MODBUS': 2, 'MITSUBISHI': 3, 'OPC': 4, 'BACNET': 5 };
      // Handle both string and numeric plcType
      const plcTypeValue = typeof plc.plcType === 'number' ? plc.plcType : (plcTypeMap[plc.plcType] || 1);
      const payload = {
        plcName: plc.plcName,
        plcAddress: plc.plcAddress,
        slotNo: plc.slotNo || 0,
        isActive: plc.isActive !== undefined ? plc.isActive : true,
        moduleId: plc.moduleId || 0,
        plcType: plcTypeValue,
        companyId: companyId
      };
      console.log('[createPlc] Input:', plc, 'Payload:', payload);
      return await tryRmmsRequest(`/Tags/AddPlc`, { headers: { 'Content-Type': 'application/json' } }, 'post', payload);
    },

    updatePlc: async (plc) => {
      const { companyId } = getContext();
      const plcTypeMap = { 'SIEMENS': 1, 'MODBUS': 2, 'MITSUBISHI': 3, 'OPC': 4, 'BACNET': 5 };
      // Handle both string and numeric plcType
      const plcTypeValue = typeof plc.plcType === 'number' ? plc.plcType : (plcTypeMap[plc.plcType] || 1);
      const payload = {
        plcId: plc.plcId,
        plcName: plc.plcName,
        plcAddress: plc.plcAddress,
        slotNo: plc.slotNo || 0,
        isActive: plc.isActive !== undefined ? plc.isActive : true,
        moduleId: plc.moduleId || 0,
        plcType: plcTypeValue,
        companyId: companyId
      };
      console.log('[updatePlc] Input:', plc, 'Payload:', payload);
      return await tryRmmsRequest(`/Tags/EditPlc`, { headers: { 'Content-Type': 'application/json' } }, 'post', payload);
    },

    deletePlc: async (plcId) => {
      console.log('[deletePlc] plcId:', plcId);
      return await tryRmmsRequest(`/Tags/DeletePlc`, { headers: { 'Content-Type': 'application/json' } }, 'post', { plcId });
    },

    // Channel Management
    getChannelsByPlc: async (plcId) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/channels/plc/${plcId}?companyId=${companyId}`, {}, 'get');
    },

    getAllChannels: async () => {
      const { companyId } = getContext();
      if (!companyId) throw new Error("Company ID is required");
      return await tryRmmsRequest(`/datasource/channels/list?companyId=${companyId}`, {}, 'get');
    },

    createChannel: async (channel) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/channels`, { headers: { 'Content-Type': 'application/json' } }, 'post', { ...channel, companyId });
    },

    updateChannel: async (channel) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/channels/${channel.channelId}`, { headers: { 'Content-Type': 'application/json' } }, 'put', { ...channel, companyId });
    },

    deleteChannel: async (channelId) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/channels/${channelId}?companyId=${companyId}`, {}, 'delete');
    },

    // Tag Management
    getTagsByChannel: async (channelId) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/tags/channel/${channelId}?companyId=${companyId}`, {}, 'get');
    },

    getAllTags: async () => {
      const { companyId } = getContext();
      if (!companyId) throw new Error("Company ID is required");
      return await tryRmmsRequest(`/datasource/tags/list?companyId=${companyId}`, {}, 'get');
    },

    createTag: async (tag) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/tags`, { headers: { 'Content-Type': 'application/json' } }, 'post', { ...tag, companyId });
    },

    updateTag: async (tag) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/tags/${tag.tagId}`, { headers: { 'Content-Type': 'application/json' } }, 'put', { ...tag, companyId });
    },

    deleteTag: async (tagId) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/tags/${tagId}?companyId=${companyId}`, {}, 'delete');
    },

    writeTagValue: async (tagId, value) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/tags/write`, { headers: { 'Content-Type': 'application/json' } }, 'post', { tagId, value, companyId });
    },

    getRealTimeTagData: async (tagIds) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/tags/realtime`, { headers: { 'Content-Type': 'application/json' } }, 'post', { tagIds, companyId });
    },

    // Get tag values from TagValue table (for real-time display)
    getTagValues: async (tagIds) => {
      // API expects List<Tag> with TagId as string
      const payload = tagIds.map(id => ({ TagId: String(id) }));
      return await tryRmmsRequest(`/Tags/GetTagValues`, { headers: { 'Content-Type': 'application/json' } }, 'post', payload);
    },

    // Get var values from VARVAL_VW view (for real-time display)
    getVarValues: async (varIds) => {
      const { companyId } = getContext();
      const payload = varIds.map(id => ({ varId: id, companyId }));
      return await tryRmmsRequest(`/Tags/GetVarValues`, { headers: { 'Content-Type': 'application/json' } }, 'post', payload);
    },

    // OPC Management
    getAllOpcServers: async () => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/opc?companyId=${companyId}`, {}, 'get');
    },

    createOpcServer: async (server) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/opc`, { headers: { 'Content-Type': 'application/json' }
      }, 'post', { ...server, companyId });
    },

    updateOpcServer: async (server) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/opc/${server.id}`, { headers: { 'Content-Type': 'application/json' } }, 'put', { ...server, companyId });
    },

    deleteOpcServer: async (serverId) => {
      const { companyId } = getContext();
      return await tryRmmsRequest(`/datasource/opc/${serverId}?companyId=${companyId}`, {}, 'delete');
    },
  },
};

export default api;
